import { NavLink } from 'react-router-dom';
import { useUIStore } from '../../stores/uiStore';
import { useAuthStore, isSuperAdmin } from '../../stores/authStore';
import {
  DashboardIcon,
  UsersIcon,
  AgentsIcon,
  SessionsIcon,
  RateLimitsIcon,
  AuditLogsIcon,
  HealthIcon,
  BetaAccessIcon,
  AdminsIcon,
  SettingsIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '../icons';
import './Sidebar.css';

/**
 * Navigation item interface
 */
interface NavItem {
  path: string;
  label: string;
  icon: React.ElementType;
  superAdminOnly?: boolean;
}

/**
 * Navigation items configuration
 */
const navItems: NavItem[] = [
  { path: '/dashboard', label: 'Dashboard', icon: DashboardIcon },
  { path: '/users', label: 'Users', icon: UsersIcon },
  { path: '/agents', label: 'Agents', icon: AgentsIcon },
  { path: '/sessions', label: 'Sessions', icon: SessionsIcon },
  { path: '/rate-limits', label: 'Rate Limits', icon: RateLimitsIcon },
  { path: '/audit-logs', label: 'Audit Logs', icon: AuditLogsIcon },
  { path: '/health', label: 'Health', icon: HealthIcon },
  { path: '/beta-access', label: 'Beta Access', icon: BetaAccessIcon },
  { path: '/admins', label: 'Admins', icon: AdminsIcon, superAdminOnly: true },
  { path: '/settings', label: 'Settings', icon: SettingsIcon },
];

/**
 * Sidebar navigation component.
 * Provides navigation links and collapsible behavior.
 * Adapts to mobile with overlay behavior.
 * 
 * Requirements: 10.1, 10.2
 */
export function Sidebar() {
  const { sidebarOpen, sidebarCollapsed, setSidebarOpen, setSidebarCollapsed } =
    useUIStore();
  const { admin } = useAuthStore();
  const isSuper = isSuperAdmin(admin);

  const handleNavClick = () => {
    // Close sidebar on mobile after navigation
    if (window.innerWidth < 768) {
      setSidebarOpen(false);
    }
  };

  const toggleCollapse = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  return (
    <aside
      className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''} ${
        sidebarOpen ? 'open' : ''
      }`}
    >
      <div className="sidebar-header">
        <div className="sidebar-logo">
          <img 
            src="/whss-logo-no-bg.png" 
            alt="WHSS Admin" 
            className="sidebar-logo-img"
            height={40}
          />
          {!sidebarCollapsed && <span className="sidebar-logo-text">WHSS Admin</span>}
        </div>
        <button
          className="sidebar-collapse-btn"
          onClick={toggleCollapse}
          aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {sidebarCollapsed ? <ChevronRightIcon size={20} /> : <ChevronLeftIcon size={20} />}
        </button>
      </div>

      <nav className="sidebar-nav">
        <ul className="sidebar-nav-list">
          {navItems.map((item) => {
            // Skip super admin only items for regular admins
            if (item.superAdminOnly && !isSuper) {
              return null;
            }

            const Icon = item.icon;

            return (
              <li key={item.path} className="sidebar-nav-item">
                <NavLink
                  to={item.path}
                  className={({ isActive }) =>
                    `sidebar-nav-link ${isActive ? 'active' : ''}`
                  }
                  onClick={handleNavClick}
                  title={item.label}
                >
                  <span className="sidebar-nav-icon">
                    <Icon size={20} />
                  </span>
                  {!sidebarCollapsed && (
                    <span className="sidebar-nav-label">{item.label}</span>
                  )}
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>

      <div className="sidebar-footer">
        {!sidebarCollapsed && (
          <div className="sidebar-version">
            v0.8.1
          </div>
        )}
      </div>
    </aside>
  );
}
